/**
 * System Admin FeatureTierProvider
 * 
 * Hardcoded system admin defaults — no testing tools needed.
 * System admins always have full access (isAdmin=true, tier=2, Wildland module enabled).
 * lookupAdminScope is hardcoded to 'system'.
 */

import React, { createContext, useContext } from 'react';
import type { UserRole, LookupScope } from '@ui-admin/contexts/FeatureTierContext';

interface FeatureTierState {
  isAdmin: boolean;
  setIsAdmin: (v: boolean) => void;
  isTier2: boolean;
  setIsTier2: (v: boolean) => void;
  hasPermitModule: boolean;
  setHasPermitModule: (v: boolean) => void;
  userRole: UserRole;
  setUserRole: (role: UserRole) => void;
  tier: number;
  setTier: (tier: number) => void;
  enabledModules: string[];
  setEnabledModules: (modules: string[] | ((prev: string[]) => string[])) => void;
  hasModule: (moduleKey: string) => boolean;
  lookupAdminScope: LookupScope;
  setLookupAdminScope: (scope: LookupScope) => void;
  lookupValueScopeFilter: LookupScope | 'all';
  setLookupValueScopeFilter: (scope: LookupScope | 'all') => void;
}

const noop = () => {};

const SYSTEM_ADMIN_STATE: FeatureTierState = {
  isAdmin: true,
  setIsAdmin: noop,
  isTier2: true,
  setIsTier2: noop,
  hasPermitModule: true,
  setHasPermitModule: noop,
  userRole: 'admin',
  setUserRole: noop,
  tier: 2,
  setTier: noop,
  enabledModules: ['wildland'],
  setEnabledModules: noop,
  hasModule: () => true,
  lookupAdminScope: 'system',
  setLookupAdminScope: noop,
  lookupValueScopeFilter: 'all',
  setLookupValueScopeFilter: noop,
};

const FeatureTierContext = createContext<FeatureTierState>(SYSTEM_ADMIN_STATE);

export function FeatureTierProvider({ children }: { children: React.ReactNode }) {
  return (
    <FeatureTierContext.Provider value={SYSTEM_ADMIN_STATE}>
      {children}
    </FeatureTierContext.Provider>
  );
}

export function useFeatureTier() {
  return useContext(FeatureTierContext);
}
